(ns web3.components.card
  (:require [web3.view :as v]
            [reagent.core :as r]
            [re-frame.core :as rf]
            [web3.i18n :as i]
            [web3.ui-state :as ui-state]
            [web3.view.hiccup :as hiccup]
            [web3.icons.shapes :as shapes]
            [web3.icons.actions :as actions]
            [kitchen-async.promise :as p]
            [clojure.string :as str]
            [web3.routing.utils :as r-utils]
            [web3.utils :as utils]
            [web3.log :as log]))

(def group :div.pad-v-3.width-100p.flex-none.flex.items-center)

(declare card)

(def media-ratio 0.854545454545455)

(def default-card-width 110)
(def default-media-height 82)
(def default-card-margin 3.5)

(def item-grid-props {:key "item-grid"})

(def file-grid-props {:key "file-grid"})

(def notes-grid-props (-> file-grid-props
                          (assoc :card-width (* 3 default-card-width)
                                 :no-media true
                                 :card-height 100)))

(def curated-grid-props {:card-width 150
                         :key "curated-grid"})

(defn card-detail
  "White card with shadow and close icon"
  [{:keys [close!]} & content]
  (into [:div.width-100p.pad-all-3.bg-darken-1.margin-v-2.margin-h-1.relative
         #_{:style {:margin-left "-1.25rem"
                    :margin-right "-1.25rem"}}
         [:div.absolute.top-0.right-0.pad-all-3.text-dark-gray.hover-text-black-hover.cursor-pointer
          {:on-click close!} shapes/close-x]]
        content))

(def card-detail-indicator
  "Arrow points from card to detail screen"
  [:div.absolute.left-0.right-0
   {:style {:bottom -11
            :border-left "10px solid transparent"
            :border-right "10px solid transparent"
            :border-top "10px solid rgba(0,0,0,0.03)"
            :margin-left -10
            :left "50%"
            :width 0
            :height 0
            :transform "rotate(180deg)"}}])

(v/defview card-grid
           "Render a grid of cards, given a desired margin and card width.

           CSS grids only reach ~75% of browsers, so we use a JS approach."
           [{:keys [card-margin card-width card-height
                    before after
                    min-card-count
                    class
                    add-card-props
                    no-media]
             :or {card-margin default-card-margin
                  card-width default-card-width}} card-props-list]
           (let [{:keys [state]} (r/current-component)
                 {{:keys [expanded-key
                          expanded-view]} :expanded-data
                  element :element} @state
                 min-card-count (max min-card-count 3)
                 mobile? (< (:window/inner-width @ui-state/ui-globals) ui-state/MOBILE_BREAKPOINT)
                 grid-margin-h 4]

             [:div.width-100p
              {:key "card-grid"
               :class class
               :ref #(when (and % (not element))
                       (swap! state assoc :element %))}

              ;; only render the element after we've been able to calculate
              ;; the available width, based on this component's root element.
              ;; (window-width is not enough info)

              (when element
                (let [container-width (-> (.-clientWidth element)
                                          (-
                                            (* 2 grid-margin-h)
                                            2))
                      [card-width
                       column-count] (if mobile?
                                       (let [column-count (cond (< container-width 350) 2
                                                                (< container-width 500) 3
                                                                :else 4)
                                             card-width (-> container-width
                                                            (/ column-count)
                                                            (js/Math.floor)
                                                            (- (* 2 card-margin)))]
                                         [card-width column-count])
                                       [card-width (let [total-card-width (+ card-width (* 2 card-margin))]
                                                     (-> (js/Math.floor (/ container-width
                                                                           total-card-width))
                                                         (min (max min-card-count (count card-props-list)))))])]
                  (when-let [card-rows (and (pos? column-count)
                                            (->> card-props-list
                                                 (partition column-count column-count nil)))]
                    [:div.flex.flex-wrap.items-center.items-stretch
                     {:style {:margin-left grid-margin-h
                              :margin-right grid-margin-h}}
                     before
                     (into [:<>]
                           (for [cards card-rows
                                 :let [row-keys
                                       (map #(or (:key %) (:id %)) cards)

                                       row-cards
                                       (for [{:keys [key id expanded-view expand-when href __typename] :as card-props } cards
                                             :let [key (or key id)]]
                                         [card (merge {:key key
                                                       :width card-width
                                                       :height card-height
                                                       :margin card-margin
                                                       :no-media no-media
                                                       :expanded? (and key (= key expanded-key))
                                                       :__typename __typename}
                                                      card-props
                                                      (when (and expanded-view (not (utils/is-external-href? href)))
                                                        ;; if card contains an :expanded-view prop, we override any other
                                                        ;; :on-click or :href action and render its view
                                                        {:on-click (fn [e]
                                                                     (p/let [should-expand? (if expand-when (expand-when)
                                                                                                            true)]
                                                                            (prn :should-expand? should-expand?)
                                                                            (when (and expanded-view should-expand?)
                                                                              (.preventDefault e)
                                                                              (if (= (:expanded-key (:expanded-data @state)) key)
                                                                                (swap! state dissoc :expanded-data)
                                                                                (swap! state assoc :expanded-data
                                                                                       {:expanded-key key
                                                                                        :expanded-view [expanded-view (-> card-props
                                                                                                                          (dissoc :expanded-view
                                                                                                                                  :icon))]})))))}))])]]

                             (cond-> (into [:<>] row-cards)
                                     (and expanded-view
                                          (contains? (set row-keys) expanded-key))
                                     (into [[card-detail {:key "expanded-view"
                                                          :close! #(swap! state dissoc :expanded-data)}
                                             expanded-view]]))))
                     (when add-card-props
                       [:div.flex.items-center.justify-center.flex-column
                        {:style {:width card-width
                                 :height card-height
                                 :margin card-margin}
                         :class (if (> card-width 100)
                                  "text-size-14"
                                  "text-size-10")}

                        (let [size (* (min (- (* card-width media-ratio)
                                              10)
                                           150)
                                      0.6)]
                          [(if (:href add-card-props)
                             :a :div)
                           (v/merge-props
                             (dissoc add-card-props :label)
                             {:class "round-100p bg-darken-1 flex-none flex items-center
                         justify-center hover-bg-darken-3
                         text-dark-gray cursor-pointer border-dashed"
                              :style {:width size
                                      :height size
                                      :margin-top (* 2 card-margin)
                                      :margin-bottom 0}})
                           (-> actions/add
                               (hiccup/size (* size 0.3)))])
                        [:div.pad-v-2.text-dark-gray (:label add-card-props (i/get :i/add))]])
                     after])))]))

(defn round-icon [props icon]
  [:div.round-100p.flex.items-center.justify-center.absolute.top-0.right-0.margin-all-2.z-1.round-icon.text-white
   (update props :style merge {:width "22px"
                               :height "22px"})
   icon])

(v/defview card
           "Render a card, given fixed width and media-height."
           {:get-initial-state
            (fn [{{createdAt :createdAt} :props}]
              (let [now (.now js/Date)
                    then (js/Date.parse createdAt)]
                (when (< (- now then) 200)
                  {:newly-created? true})))
            :component-did-mount
            (fn [{:keys [state]}]
              (when (:newly-created? @state) (swap! state dissoc :newly-created?)))}
           [{:keys [
                    ;; text
                    label
                    label-size
                    label-lines

                    sub-label
                    no-text
                    label-inverted
                    center-vert?

                    ;; action
                    href
                    on-click
                    on-delete

                    ;; layout
                    width
                    height
                    margin
                    card-style
                    wrap?
                    class
                    style

                    ;; media
                    image
                    icon
                    media
                    image-fit

                    badge

                    ;; media format
                    media-divider?
                    media-class
                    no-media

                    expanded?
                    json-ld

                    id
                    __typename]
             :as this
             :or {image-fit "contain"
                  wrap? false
                  center-vert? true
                  width 110
                  margin 7
                  card-style :white-shadow
                  label-size 14}} & content]

           (let [media-height (cond no-text height
                                    no-media 0
                                    :else (* media-ratio width))
                 external-link? (utils/is-external-href? href)
                 {:keys [newly-created?]} @(:state (r/current-component))
                 recommended? (= card-style :recommended)]
             [(if external-link? :a :div)
              (cond-> {:key (or id label)
                       :on-click (or on-click
                                     ;; using on-click because .preventDefault/.stopPropagation do not seem to work on the contained delete icon
                                     (when (and (not external-link?)
                                                href)
                                       #(rf/dispatch [:routing/nav href])))
                       :class (str class
                                   " text-size-14 weight-medium text-black relative text-center round-3 inline-flex flex-column"
                                   (when (or href on-click)
                                     " cursor-pointer")
                                   (case card-style
                                     :recommended " card-recommended"
                                     :white-shadow " shadow-card shadow-card-hover bg-white hover-grow-1 "
                                     :dashed-gray " dashed-gray"))
                       :style (-> {:width width
                                   :height height
                                   :margin margin
                                   :transition "all 0.2s ease"}
                                  (cond-> newly-created? (assoc :transform "translate3d(-50px, -10px, -10px) scale(0.9)"
                                                                :opacity 0.5))
                                  (merge style))}
                      external-link? (assoc :href href
                                            :target "_blank"))
              (when expanded?
                card-detail-indicator)
              ;; edit
              (when on-delete
                (round-icon {:on-click #(do (.stopPropagation %)
                                            (on-delete %))
                             :class "absolute top-0 right-0 margin-all-2 z-1 cursor-pointer bg-black hover-bg-black-hover"}
                            actions/delete))

              ;; media
              [:div.flex.relative.flex-none.justify-center
               {:style {:height media-height
                        :padding-bottom (when recommended? 10)}
                :class (str media-class
                            (when center-vert? " items-center"))}
               json-ld
               (when badge
                 [:div.absolute.pad-all-2.top-0.left-0 badge])

               ;; media can be an image _or_ icon, we only render one.
               (cond image
                     [:div {:style {:width width
                                    :height media-height
                                    :background-image (str "url(" image ")")
                                    :background-size image-fit
                                    :background-repeat "no-repeat"
                                    :background-position "center center"}}]
                     icon
                     [:div.items-center.justify-center.flex.width-100p
                      {:class (when recommended? " opacity-40")}
                      icon
                      #_(hiccup/size icon
                                     (* 0.4 width)
                                     (* 0.8 media-height))]
                     media
                     media)

               (when (and (not no-text) media-divider?)
                 ;; TODO
                 ;; work on spacing when we have good sample data on hand
                 [:div.width-50p.absolute.bottom-0
                  {:style {:border-bottom "1px solid rgba(0,0,0,0.15)"}}])]

              ;; text takes up space not claimed by media
              (when-not no-text
                [:div.flex.text-center.pad-h-2.justify-center.flex-auto.relative
                 {:class (str (when label-inverted " bg-black text-white")
                              (when recommended? " text-dark-gray")
                              (when center-vert? " items-center"))}
                 (when recommended?
                   (round-icon {:class "absolute text-real-black bg-darken-1"
                                :style {:top 0
                                        :left "50%"
                                        :margin-left -11
                                        :margin-top -15}}
                               (-> actions/add
                                   (hiccup/size 9))))
                 (into [:div.text-center.width-100p.pad-v-2
                        #_(when-not wrap? {:class "truncate"})]
                       (let [label [:div
                                    {:style {:font-size label-size
                                             :line-height (str (* label-size 1.2) "px")
                                             :color (if (= __typename "link") "red" "inherit" )
                                             :-webkit-text-fill-color (if (= __typename "link") "red" "inherit" )}
                                     :class (if label-lines
                                              (str "truncate-multiline-" label-lines)
                                              "truncate pad-v-1")}
                                    label]]
                         (if sub-label
                           [label
                            [:div.truncate.text-dark-gray.text-size-12.pad-v-1.margin-top-n1 sub-label]]
                           [label])))])]))